import { Injectable } from '@angular/core';
import { MD5 } from 'crypto-js';
import { Customer } from '@core/models/invoice-info';
import { ActivatedRoute, Router } from '@angular/router';

const parsePathFromRoute = (router: Router) => {
  try {
    const url: string = router.url;
    if (url.match(/itemlist/)) {
      return '/itemlist'
    } else if(url.match(/login/)) {
      return '/login'
    } else if(url.match(/returns/)) {
      return '/returns'
    }
    return url;
  } catch (e) {
    return "";
  }
};

@Injectable({
  providedIn: 'root',
})
export class UserAnalyticsService {
  constructor(private router: Router) {}

  startUserAnalyticsSession(
    customer: Customer,
    tenantId: string | undefined,
    brandExternalId: string | undefined
  ) {
    const uniqueUserAnalyticsIdentifier = customer.phone || customer.email;
    const userId = MD5(uniqueUserAnalyticsIdentifier!).toString();

    if (!userId) {
      return;
    }

    // Wait for Pendo to load (it's loaded asynchronously from CDN)
    let attempts = 0;
    const maxAttempts = 30; // 3 seconds with 100ms intervals

    const initPendo = setInterval(() => {
      const pendoInstance = (window as any).pendo;

      if (pendoInstance && typeof pendoInstance.initialize === 'function') {
        clearInterval(initPendo);

        try {
          const analyticsInitPayload = {
            visitor: {
              id: userId,
              brand: brandExternalId,
              module: `hosted-returns`,
            },
            account: {
              id: tenantId
            }
          };
          // Initialize Pendo with the User ID
          pendoInstance.initialize(analyticsInitPayload);

          // console.log('✅ P initialized successfully', JSON.stringify(analyticsInitPayload));
        } catch (e) {
          console.error('❌ Failed to initialize P:', e);
        }
      } else if (++attempts >= maxAttempts) {
        clearInterval(initPendo);
        console.warn('⚠️ Pendo script did not load within 3 seconds - analytics disabled');
      }
    }, 100);
  }

  captureUserEvent(captureEvent: string, path?: string) {
    console.log('Attempting to capture event:', captureEvent, 'on path:', path);
    if (!captureEvent) {
      return;
    }

    try {
      captureEvent = this.validateCapturedEvent(captureEvent);

      if (!path) {
        path = parsePathFromRoute(this.router);
      }

      // Check window.pendo instead of global pendo variable
      // This handles async loading of Pendo script from CDN
      const pendoInstance = (window as any).pendo;
      if (pendoInstance && typeof pendoInstance.track === 'function') {
        // Track the main event
        pendoInstance.track(captureEvent, {
          page: path,
        });

        // Also track the page view for analytics
        pendoInstance.track("Page-" + path, {
          event: captureEvent,
        });

        // console.log('✅ Event tracked:', captureEvent);
      } else if (captureEvent !== 'app-init') {
        // Only warn for non-initialization events
        console.warn('⚠️ P not available:', captureEvent);
      }
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
      // Don't throw - don't break app if analytics fails
    }
  }
  private validateCapturedEvent(captureEvent: string) {
    captureEvent = captureEvent
      .replace(/_/g, '-')
      .replace(/\s+/g, '-')
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase();
    return captureEvent;
  }
}
